import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@vtak/shared';
import { KeyRound, LogOut, ShieldQuestion, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/auth/AuthProvider';
import { workspaceClient } from '@/services/workspace-client';
import vtakLogo from '@/assets/vtak-logo.png';

/**
 * Full-screen entry shown to a user with no memberships. Redeem a join code to
 * request access; the request lands as pending for a unit admin to approve.
 */
export function JoinScreen(): React.JSX.Element {
  const { logout } = useAuth();
  const [code, setCode] = useState('');
  const queryClient = useQueryClient();

  const join = useMutation({
    mutationFn: () => workspaceClient.join(code.trim()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.workspace.memberships });
    },
  });

  const canSubmit = code.trim().length > 0 && !join.isPending;

  return (
    <ScreenShell>
      <div className="mb-6 flex flex-col items-center gap-3">
        <img src={vtakLogo} alt="VTAK" className="h-12 w-auto" />
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Join a unit</p>
      </div>

      {join.isSuccess ? (
        <SubmittedState onSignOut={logout} />
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) join.mutate();
          }}
          className="flex flex-col gap-2"
        >
          <label htmlFor="join-code" className="text-xs font-medium text-muted-foreground">
            Join code
          </label>
          <div className="flex gap-2">
            <Input
              id="join-code"
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="XXXX-XXXX-XXXX"
              className="font-mono tracking-widest"
            />
            <Button type="submit" disabled={!canSubmit}>
              {join.isPending ? 'Joining…' : 'Join'}
            </Button>
          </div>
          {join.isError && (
            <p className="flex items-center gap-1.5 text-xs text-red-400">
              <ShieldQuestion className="h-3.5 w-3.5" />
              Invalid or expired code. Ask a unit admin for a new one.
            </p>
          )}
        </form>
      )}

      <RequestAccessSection />

      <button
        onClick={logout}
        className="mt-6 inline-flex items-center justify-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        <LogOut className="h-3.5 w-3.5" />
        Sign out
      </button>
    </ScreenShell>
  );
}

/** Success state after a join request is submitted */
function SubmittedState({ onSignOut }: { onSignOut: () => void }): React.JSX.Element {
  return (
    <div className="flex flex-col items-center gap-2 rounded-md border border-green-500/30 bg-green-500/5 px-4 py-5 text-center">
      <CheckCircle2 className="h-6 w-6 text-green-400" />
      <p className="text-sm font-medium text-foreground">Request submitted</p>
      <p className="text-xs text-muted-foreground">
        Your membership is pending approval. You&apos;ll gain full access once an admin approves it.
      </p>
      <button
        onClick={onSignOut}
        className="mt-1 text-xs text-muted-foreground underline-offset-2 hover:underline"
      >
        Sign out
      </button>
    </div>
  );
}

/** Muted list of organizations, hinting how to request access */
function RequestAccessSection(): React.JSX.Element {
  const orgs = useQuery({ queryKey: queryKeys.workspace.orgs, queryFn: () => workspaceClient.getOrgs() });

  return (
    <div className="mt-6 border-t border-echo-border pt-4">
      <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        Organizations
      </p>
      {orgs.isLoading && <p className="text-xs text-muted-foreground">Loading…</p>}
      {orgs.data && orgs.data.length === 0 && (
        <p className="text-xs text-muted-foreground">No organizations available.</p>
      )}
      <ul className="flex flex-col gap-1">
        {orgs.data?.map((org) => (
          <li
            key={org.id}
            title="Ask a unit admin for a join code"
            className="flex cursor-default items-center gap-2 rounded px-2 py-1 text-xs text-muted-foreground"
          >
            <KeyRound className="h-3 w-3 opacity-60" />
            {org.name}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Full-screen dark shell hosting a single centered card */
function ScreenShell({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="fixed inset-0 z-[2000] grid place-items-center bg-[#0d0d0d] p-4">
      <div className="w-full max-w-sm rounded-xl border border-echo-border bg-[#1a1a1a] p-6 shadow-2xl">
        {children}
      </div>
    </div>
  );
}
